import { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Badge, Button, Card, CardHeader, Input } from '@/components/common';
import { usePOSPCompliance, useAdvancePOSPCompliance } from '@/hooks/useBrokers';
import { formatDate } from '@/utils';
import { CHANNEL_TRAINING_DOCUMENT_TYPES } from '@/services/kycDocuments';
import { ChannelKycDocumentsPanel } from './ChannelKycDocumentsPanel';
import type { POSPComplianceUpdate } from '@/types';

/** What counts as a pass, matching `POSPTraining.passing_score`'s default.
 *  Display/derivation only — nothing refuses a lower score. */
const EXAM_PASS_MARK = 60;

/**
 * P1 — Training & Certification.
 *
 * ONE component, two audiences: the Broker Admin's POSP detail (the channel
 * page's POSP branch), where it carries the controls, and the agent's own
 * Settings > Certifications, where `canManage` is false and it is a record
 * they read. Same endpoint, same derived facts, so the two screens cannot
 * disagree about whether an agent's certificate has expired.
 *
 * Everything on it was already columns on `POSP` that nothing ever wrote:
 * `irdai_registration_number`, `certification_date`, `certification_expiry`,
 * `training_completed`, `exam_passed`, `exam_score`. The stage, the hours and
 * the timestamps are new, and broker staff step them by hand — there is no LMS
 * content behind `POSPTraining`, so an automatic stepper would be a progress
 * bar for work nobody did.
 *
 * NOTHING IS GATED ON THIS (decision 15). All 81 live agents have blank
 * compliance and a gate would have locked every one of them out, so the card
 * says so in as many words rather than leaving an admin to guess whether an
 * unfinished agent has stopped working.
 *
 * `certification_days_remaining === null` means NO expiry is recorded. That is
 * not "expired" and is never drawn as one — it is the state every live agent
 * is in today.
 */

function ExpiryMarker({
  days,
  expired,
}: {
  days: number | null;
  expired: boolean;
}) {
  if (days === null) {
    return <span className="text-xs text-gray-500 dark:text-gray-400">No expiry recorded</span>;
  }
  if (expired) {
    return (
      <Badge variant="error" size="sm">
        Expired {Math.abs(days)} day{Math.abs(days) === 1 ? '' : 's'} ago
      </Badge>
    );
  }
  return (
    <Badge variant={days <= 30 ? 'warning' : 'success'} size="sm">
      Expires in {days} day{days === 1 ? '' : 's'}
    </Badge>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
}

/** The stage chain, with the one that has been reached marked. Drawn as a list
 *  rather than a progress bar: five named stages a person stepped by hand are
 *  facts, not a percentage. */
function StageTrail({
  stages,
  current,
}: {
  stages: { value: string; label: string; reached: boolean }[] | undefined;
  current: string;
}) {
  // A payload without the list is a malformed answer, not an empty lifecycle.
  // Say so rather than white-screening a profile page over a missing key.
  if (!stages?.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        The stage list could not be read from this record.
      </p>
    );
  }
  return (
    <ol className="space-y-2">
      {stages.map((stage) => (
        <li key={stage.value} className="flex items-center gap-2">
          {stage.reached ? (
            <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
          ) : (
            <span className="h-4 w-4 flex-shrink-0 rounded-full border border-gray-300 dark:border-gray-600" />
          )}
          <span
            className={
              stage.value === current
                ? 'text-sm font-semibold text-gray-900 dark:text-gray-100'
                : stage.reached
                  ? 'text-sm text-gray-600 dark:text-gray-300'
                  : 'text-sm text-gray-400 dark:text-gray-500'
            }
          >
            {stage.label}
          </span>
        </li>
      ))}
    </ol>
  );
}

export function PospTrainingPanel({
  pospId,
  canManage,
  title = 'Training & Certification',
}: {
  pospId: string;
  /** Broker staff. The server refuses an agent regardless; this keeps controls
   *  they could never use off their screen. */
  canManage: boolean;
  /** The agent's own Settings page calls this section "Certifications", which
   *  is the name the request uses and the one their sidebar already shows. Same
   *  component, same data — only the heading differs. */
  title?: string;
}) {
  const { data, isLoading, isError, error, refetch } = usePOSPCompliance(pospId);
  const advance = useAdvancePOSPCompliance(pospId);

  const [hours, setHours] = useState('');
  const [irdai, setIrdai] = useState('');
  const [certifiedOn, setCertifiedOn] = useState('');
  const [expiry, setExpiry] = useState('');
  const [examScore, setExamScore] = useState('');

  // Repaint the form from the server's answer whenever it changes — the POST
  // returns the STORED record, so the inputs must follow it rather than keep
  // whatever was typed into a request that was partly refused.
  useEffect(() => {
    if (!data) return;
    setHours(data.training_hours_completed);
    setIrdai(data.irdai_registration_number ?? '');
    setCertifiedOn(data.certification_date ?? '');
    setExpiry(data.certification_expiry ?? '');
    setExamScore(data.exam_score === null ? '' : String(data.exam_score));
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader title={title} />
        <p className="py-4 text-sm text-gray-500 dark:text-gray-400">Loading compliance record…</p>
      </Card>
    );
  }

  // A failed request is a failure, never an agent with nothing on file.
  if (isError || !data) {
    return (
      <Card>
        <CardHeader title={title} />
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-900/20">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-300">
              This compliance record could not be loaded — that is not the same as there being
              nothing recorded.
            </p>
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">
              {error instanceof Error ? error.message : 'The request failed.'}
            </p>
            <Button size="sm" variant="outline" className="mt-2" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const notAssigned = data.compliance_status === '';

  /** Only the fields the admin actually EDITED.
   *
   *  Posting the whole form back overwrote facts nobody touched: a blank hours
   *  box became "0", and `exam_passed` was recomputed from the score box on
   *  every save — so an agent certified before this platform existed, with
   *  `exam_passed` set by hand and no score, lost their recorded pass the first
   *  time a Broker Admin opened the card to type an IRDAI number. The serializer
   *  is fully partial, so an omitted key is the correct no-op.
   */
  // An arrow, not a declaration: a hoisted function would be outside the
  // narrowing that already proved `data` is loaded above.
  const editedFields = (): POSPComplianceUpdate => {
    const payload: POSPComplianceUpdate = {};
    // Hours is NOT NULL on the row, so a cleared box means "I did not touch
    // this", never "record zero".
    if (hours !== '' && Number(hours) !== Number(data.training_hours_completed)) {
      payload.training_hours_completed = hours;
    }
    if (irdai !== (data.irdai_registration_number ?? '')) {
      payload.irdai_registration_number = irdai;
    }
    if (certifiedOn !== (data.certification_date ?? '')) {
      payload.certification_date = certifiedOn === '' ? null : certifiedOn;
    }
    if (expiry !== (data.certification_expiry ?? '')) {
      payload.certification_expiry = expiry === '' ? null : expiry;
    }
    // The pass travels WITH the score and only with it: a score is what makes
    // the exam a pass here, so touching neither leaves both alone.
    const recordedScore = data.exam_score === null ? '' : String(data.exam_score);
    if (examScore !== recordedScore) {
      payload.exam_score = examScore === '' ? null : Number(examScore);
      payload.exam_passed = examScore !== '' && Number(examScore) >= EXAM_PASS_MARK;
    }
    return payload;
  };

  return (
    <Card>
      <CardHeader
        title={title}
        subtitle={
          canManage
            ? 'The IRDAI training record your staff keep for this agent. Nothing here restricts what they can sell.'
            : 'Your IRDAI training record, as your brokerage keeps it. Your brokerage updates it — nothing here restricts what you can sell.'
        }
        action={
          <Badge variant={notAssigned ? 'default' : data.compliance_status === 'completed' ? 'success' : 'info'}>
            {data.compliance_status_display}
          </Badge>
        }
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Fact label="IRDAI Registration">{data.irdai_registration_number || 'Not recorded'}</Fact>
        <Fact label="Certified On">
          {data.certification_date ? formatDate(data.certification_date) : '—'}
        </Fact>
        <Fact label="Certificate Expiry">
          <div className="flex flex-wrap items-center gap-2">
            <span>{data.certification_expiry ? formatDate(data.certification_expiry) : '—'}</span>
            <ExpiryMarker days={data.certification_days_remaining} expired={data.certification_expired} />
          </div>
        </Fact>
        <Fact label="Training Hours">
          {data.training_hours_completed} of {data.training_hours_required}
          {Number(data.training_hours_outstanding) > 0 && (
            <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
              {data.training_hours_outstanding} outstanding
            </span>
          )}
        </Fact>
        <Fact label="Training">
          {/* `training_completed` is the legacy column the rest of the platform
              reads, and it moves with the final stage — so it is shown beside
              the stage rather than instead of it, and the two cannot disagree. */}
          <Badge variant={data.training_completed ? 'success' : 'warning'} size="sm">
            {data.training_completed ? 'Completed' : 'Pending'}
          </Badge>
        </Fact>
        <Fact label="Examination">
          <Badge variant={data.exam_passed ? 'success' : 'warning'} size="sm">
            {data.exam_passed
              ? `Passed${data.exam_score !== null ? ` (${data.exam_score})` : ''}`
              : 'Not recorded'}
          </Badge>
        </Fact>
        <Fact label="Training Assigned">
          {data.training_assigned_at ? formatDate(data.training_assigned_at) : '—'}
        </Fact>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Stage
          </p>
          <StageTrail stages={data.stages} current={data.compliance_status} />
        </div>

        <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Next required action
          </p>
          <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{data.next_action}</p>

          {canManage && data.next_stage && (
            <Button
              size="sm"
              className="mt-3"
              disabled={advance.isPending}
              onClick={() => advance.mutate({ compliance_status: data.next_stage ?? undefined })}
            >
              {advance.isPending ? 'Saving…' : `Move to “${data.next_stage_display}”`}
            </Button>
          )}
          {!canManage && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Your brokerage records this for you — you cannot change it here.
            </p>
          )}
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            An incomplete record does not block anything: quoting, issuing and payouts work exactly
            as they do today.
          </p>
        </div>
      </div>

      {canManage && (
        <div className="mt-6 border-t border-gray-100 pt-4 dark:border-gray-700">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            Record the facts
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Input
              label="Training hours completed"
              id="posp-training-hours"
              name="posp-training-hours"
              type="number"
              min="0"
              step="0.5"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
            <Input
              label="IRDAI registration number"
              id="posp-irdai-number"
              name="posp-irdai-number"
              value={irdai}
              onChange={(e) => setIrdai(e.target.value)}
            />
            <Input
              label="Exam score"
              id="posp-exam-score"
              name="posp-exam-score"
              type="number"
              min="0"
              max="100"
              value={examScore}
              onChange={(e) => setExamScore(e.target.value)}
              helperText={`A recorded fact, not a stage. ${EXAM_PASS_MARK} or above counts as a pass; a blank box leaves the recorded result alone.`}
            />
            <Input
              label="Certified on"
              id="posp-certified-on"
              name="posp-certified-on"
              type="date"
              value={certifiedOn}
              onChange={(e) => setCertifiedOn(e.target.value)}
            />
            <Input
              label="Certificate expiry"
              id="posp-certificate-expiry"
              name="posp-certificate-expiry"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            disabled={advance.isPending}
            onClick={() => advance.mutate(editedFields())}
          >
            {advance.isPending ? 'Saving…' : 'Save details'}
          </Button>
        </div>
      )}

      {/* The certificate itself. The SAME uploader, store and review as the
          KYC panel above it — one more slot in `ChannelDocument`, not a second
          uploader that would drift. `certificate_uploaded` is refused by the
          server until a row exists here, so this control is what unlocks the
          stage rather than a checkbox that claims it. */}
      {data.channel && (
        <div className="mt-6 border-t border-gray-100 pt-4 dark:border-gray-700">
          <ChannelKycDocumentsPanel
            channelId={data.channel}
            canVerify={canManage}
            canUpload={canManage}
            bare
            slots={CHANNEL_TRAINING_DOCUMENT_TYPES}
            title="Training Certificate"
            subtitle={
              canManage
                ? 'Upload or replace the agent’s certificate. The stage “Certificate Uploaded” cannot be set until a file is actually stored here.'
                : 'The certificate your brokerage holds for you.'
            }
          />
        </div>
      )}
    </Card>
  );
}
